// MapNodeChrome.js — DOM chrome for the pm-map-node embed (header, caption, classification).

import React from 'react';
import {createRoot} from 'react-dom/client';
import {flushSync} from 'react-dom';
import {buildViewOptions, getEffectiveDtgSelected} from './schema/mapPmAdapter';
import {BASEMAP_STYLES, DEFAULT_VIEW_ID} from './schema/mapSchema';
import {
    applyClassificationToElement,
    isClassificationBadgeClick,
    openClassificationMenuForBlock,
} from './classifiedBlockChrome';
import {pasteAsPlainText} from '../../utils/pasteAsPlainText';
import {MapNodeHeaderFields} from './MapNodeHeaderFields';
import {SitrepGenerationProgress} from './sitrep/SitrepGenerationProgress';
import {SITREP_PROGRESS_PHASE} from './sitrep/sitrepProgressState';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// Ticket 01 (map-node-dtg-view-audit): the DTG control is a plain date-only picker (no
// hours/minutes), matching useDtgRange's date-only convention. Stored as a UTC 'YYYY-MM-DD'
// string for the DatePicker's value prop (converted to a UTC dayjs in MapNodeHeaderFields).
function msToDateInputValue(ms) {
    if (ms == null) return '';
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
}

export class MapNodeChrome {
    /**
     * @param {object} opts
     * @param {object|null} opts.classificationMenu
     * @param {(viewId: string|null) => void} opts.onViewSelected
     * @param {(dtgSelectedMs: number|null) => void} [opts.onDtgSelected]
     * @param {(basemapStyleId: string) => void} [opts.onBasemapSelected]
     * @param {() => void} opts.onEdit
     * @param {(result: object) => void} opts.onClassificationChange
     * @param {(caption: string) => void} [opts.onCaptionChange]
     * @param {(opts: {additionalContext: string, windowDays: number, maxCheckpoints: number|null}) => Promise<void>|void} [opts.onGenerateSitrep]
     */
    constructor(opts = {}) {
        this.classificationMenu = opts.classificationMenu || null;
        this._onViewSelected = opts.onViewSelected || (() => {});
        this._onDtgSelected = opts.onDtgSelected || (() => {});
        this._onBasemapSelected = opts.onBasemapSelected || (() => {});
        this._onEdit = opts.onEdit || (() => {});
        this._onClassificationChange = opts.onClassificationChange || (() => {});
        this._onCaptionChange = opts.onCaptionChange || (() => {});
        this._onGenerateSitrep = opts.onGenerateSitrep || (() => {});

        // Header element saved so containsTarget can do a single .contains() check covering
        // all header controls (the React root renders into this div), without including the
        // map container or other chrome children that should not intercept PM interactions.
        this._headerEl = null;
        this._captionEl = null;
        this._mapContainer = null;
        this._editBtn = null;

        // Header field React state — source of truth for _renderHeader.
        this._viewOptions = buildViewOptions([]);
        this._viewValue = DEFAULT_VIEW_ID;
        this._dtgValue = '';       // 'YYYY-MM-DD' UTC string, or ''
        this._basemapValue = BASEMAP_STYLES[0].url;

        // "Actively open" flags: set true while a field's popup/menu is open, so update methods
        // skip re-syncing and avoid clobbering an in-progress user selection — replacing the old
        // `document.activeElement === nativeEl` focus check used for native inputs.
        this._viewOpen = false;
        this._dtgOpen = false;
        this._basemapOpen = false;

        // Portaled popup DOM nodes: MUI Select menus and DatePicker calendar render via a Portal
        // to document.body (required since .pm-map-node { overflow:hidden } would clip inline
        // rendering). Each ref callback is set on the popup's root DOM node via
        // MenuProps.PaperProps.ref / slotProps.popper.ref — populated when the popup mounts,
        // null'd when it unmounts — so containsTarget can detect clicks inside open popups.
        this._viewPopperEl = null;
        this._dtgPopperEl = null;
        this._basemapPopperEl = null;
        // Ticket MINSKY-16407: the sitrep button's Dialog also portals to document.body (same
        // reason as the popups above) — captured the same way so containsTarget recognizes
        // clicks inside it while it's open.
        this._sitrepDialogEl = null;

        // Stable callback refs/handlers passed as props to MapNodeHeaderFields — these are plain
        // functions (not bound class methods wrapping addEventListener) since the MUI components
        // now call them directly via React's synthetic event system.
        this._onViewChangeBound = (v) => this._onViewSelected(v || DEFAULT_VIEW_ID);
        this._onViewOpenBound = () => { this._viewOpen = true; };
        this._onViewCloseBound = () => { this._viewOpen = false; };
        this._viewPopperRef = (el) => { this._viewPopperEl = el; };

        this._onDtgChangeBound = (ms) => this._onDtgSelected(Number.isFinite(ms) ? ms : null);
        this._onDtgOpenBound = () => { this._dtgOpen = true; };
        this._onDtgCloseBound = () => { this._dtgOpen = false; };
        this._dtgPopperRef = (el) => { this._dtgPopperEl = el; };

        this._onBasemapChangeBound = (v) => this._onBasemapSelected(v);
        this._onBasemapOpenBound = () => { this._basemapOpen = true; };
        this._onBasemapCloseBound = () => { this._basemapOpen = false; };
        this._basemapPopperRef = (el) => { this._basemapPopperEl = el; };

        this._editBtnRef = (el) => { this._editBtn = el; };
        this._onEditClickBound = () => this._onEdit();

        this._sitrepDialogPopperRef = (el) => { this._sitrepDialogEl = el; };
        this._onGenerateSitrepBound = (params) => this._onGenerateSitrep(params);
        // MINSKY-16410: while a generation is in flight for this node, the header's sitrep
        // trigger is disabled (see updateSitrepInFlight) — see MapNodeView._setSitrepInFlight's
        // doc comment for why "disable" was chosen over "queue/replace" for concurrent submits.
        this._sitrepInFlight = false;
        this._sitrepProgressEl = null;
        this._sitrepProgressRoot = null;

        this._build();
    }

    _build() {
        this.dom = document.createElement('div');
        this.dom.className = 'pm-map-node';
        this.dom.setAttribute('contenteditable', 'false');

        const header = document.createElement('div');
        header.className = 'pm-map-node__header';
        this.dom.appendChild(header);
        this._headerEl = header;

        // Ticket 04 (map-node-ui-audit): the 3 fields + Edit button are a full MUI React tree
        // mounted into this header via a root created once here. `_renderHeader` wraps every
        // render in `flushSync` so ref callbacks always resolve synchronously (tests can query
        // the DOM right after `new MapNodeChrome({})` with no act()/microtask wait).
        this._headerRoot = createRoot(header);
        this._renderHeader();

        this._mapContainer = document.createElement('div');
        this._mapContainer.className = 'pm-map-node__map-container';
        this.dom.appendChild(this._mapContainer);

        // MINSKY-16410: linear progress / done / error UI for a situation-report generation,
        // rendered directly under the map embed (a plain flow-layout row, not an overlay — see
        // SitrepGenerationProgress.jsx's header comment for why this is a new element rather
        // than a restyle of the existing overlay `_queueBadge`). Owns its own tiny React root,
        // same pattern as the header's `_headerRoot`, so MapNodeView can push down plain view-state
        // props via `renderSitrepProgress()` without this chrome class needing to know anything
        // about Yjs/sitrepGenerations itself.
        this._sitrepProgressEl = document.createElement('div');
        this._sitrepProgressEl.className = 'pm-map-node__sitrep-progress-slot';
        this.dom.appendChild(this._sitrepProgressEl);
        this._sitrepProgressRoot = createRoot(this._sitrepProgressEl);

        this._captionEl = document.createElement('div');
        this._captionEl.className = 'pm-map-node__caption';
        this._captionEl.contentEditable = 'true';
        this._captionEl.setAttribute('data-placeholder', 'Add a caption…');
        this.dom.appendChild(this._captionEl);
        this._captionEl.addEventListener('blur', this._onCaptionBlur.bind(this));
        this._captionEl.addEventListener('keydown', this._onCaptionKeydown.bind(this));
        this._captionEl.addEventListener('paste', this._onCaptionPaste.bind(this));

        this._onBadgeClickBound = this._onBadgeClick.bind(this);
        this.dom.addEventListener('mousedown', this._onBadgeClickBound);
    }

    /** Re-render the header's MUI React tree from current view/DTG/basemap field state. */
    _renderHeader() {
        if (!this._headerRoot) return;
        flushSync(() => {
            this._headerRoot.render(
                <MapNodeHeaderFields
                    viewOptions={this._viewOptions}
                    viewValue={this._viewValue}
                    onViewChange={this._onViewChangeBound}
                    onViewOpen={this._onViewOpenBound}
                    onViewClose={this._onViewCloseBound}
                    viewPopperRef={this._viewPopperRef}
                    dtgValue={this._dtgValue}
                    onDtgChange={this._onDtgChangeBound}
                    onDtgOpen={this._onDtgOpenBound}
                    onDtgClose={this._onDtgCloseBound}
                    dtgPopperRef={this._dtgPopperRef}
                    basemapOptions={BASEMAP_STYLES}
                    basemapValue={this._basemapValue}
                    onBasemapChange={this._onBasemapChangeBound}
                    onBasemapOpen={this._onBasemapOpenBound}
                    onBasemapClose={this._onBasemapCloseBound}
                    basemapPopperRef={this._basemapPopperRef}
                    editBtnRef={this._editBtnRef}
                    onEditClick={this._onEditClickBound}
                    onGenerateSitrep={this._onGenerateSitrepBound}
                    sitrepDialogPopperRef={this._sitrepDialogPopperRef}
                    sitrepDisabled={this._sitrepInFlight}
                />,
            );
        });
    }

    /**
     * MINSKY-16410: toggles the header sitrep trigger's disabled state while a generation is in
     * flight for this node — see MapNodeView._setSitrepInFlight for the "disable vs queue/replace"
     * rationale. Cheap no-op re-render when the flag hasn't actually changed.
     * @param {boolean} inFlight
     */
    updateSitrepInFlight(inFlight) {
        const next = Boolean(inFlight);
        if (this._sitrepInFlight === next) return;
        this._sitrepInFlight = next;
        this._renderHeader();
    }

    /**
     * MINSKY-16410: re-renders the under-the-map progress/result UI from plain view-state props —
     * this chrome class stays agnostic of Yjs/sitrepGenerations; MapNodeView computes `viewState`
     * via sitrepProgressState.getSitrepProgressViewState and passes it straight through here.
     * @param {{phase: string, label?: string, errorMessage?: string}} viewState
     * @param {{onRetry?: Function, retryPending?: boolean, onDismissSuccess?: Function}} [handlers]
     */
    renderSitrepProgress(viewState, handlers = {}) {
        if (!this._sitrepProgressRoot) return;
        flushSync(() => {
            this._sitrepProgressRoot.render(
                <SitrepGenerationProgress
                    phase={viewState?.phase ?? SITREP_PROGRESS_PHASE.IDLE}
                    label={viewState?.label ?? ''}
                    errorMessage={viewState?.errorMessage ?? ''}
                    onRetry={handlers.onRetry}
                    retryPending={Boolean(handlers.retryPending)}
                    onDismissSuccess={handlers.onDismissSuccess}
                />,
            );
        });
    }

    get mapContainer() {
        return this._mapContainer;
    }

    /**
     * @param {object} attrs — map node attrs
     * @param {string|null} viewId
     * @param {object[]} views
     * @param {object[]} [markings] — this embed's shared-map markings, forwarded to `updateDtg`
     *   so the DTG picker can fall back to the latest available DTG (see `updateDtg`'s doc
     *   comment) when `attrs.dtgSelectedMs` is null.
     */
    updateChrome(attrs, viewId, views = [], markings = []) {
        this.applyClassification(attrs);
        this.updateViewSelect(views, viewId);
        this.updateDtg(attrs, markings);
        this.updateBasemapSelect(attrs.basemapStyleId);
        this.updateCaption(attrs.caption);
    }

    applyClassification(attrs) {
        applyClassificationToElement(this.dom, attrs);
    }

    updateViewSelect(views = [], viewId = null) {
        const options = buildViewOptions(views);
        const current = viewId || DEFAULT_VIEW_ID;
        const nextValue = options.some((o) => o.id === current) ? current : (options[0]?.id ?? '');
        // Skip re-render while the view menu is open to avoid clobbering an in-progress selection.
        if (this._viewOpen) return;
        this._viewOptions = options;
        this._viewValue = nextValue;
        this._renderHeader();
    }

    /**
     * Sync the DTG date picker from node attrs — skipped while the calendar popup is open.
     *
     * map-node-dtg-default ticket: `attrs.dtgSelectedMs` defaults to `null` ("no DTG selected"
     * yet) — rather than showing a blank picker in that case, this displays the latest available
     * DTG across `markings`' snapshot history (see `getEffectiveDtgSelected`'s doc comment for
     * why "latest" means that, not the shared map_meta range). This is a display-only fallback:
     * it does not write anything back to `attrs.dtgSelectedMs`, so it stays correct as new
     * markings/snapshots are added without a separate migration.
     * @param {object} attrs
     * @param {object[]} [markings] — this embed's shared-map markings (as from
     *   `extractMapState`/`MapFragmentStore`'s `previewState.markings`).
     */
    updateDtg(attrs = {}, markings = []) {
        if (this._dtgOpen) return;
        const effectiveMs = getEffectiveDtgSelected({markings}, attrs.dtgSelectedMs);
        const next = msToDateInputValue(effectiveMs);
        if (this._dtgValue !== next) {
            this._dtgValue = next;
            this._renderHeader();
        }
    }

    /**
     * Sync the basemap select from this node's own `basemapStyleId` attr — skipped while
     * the menu is open, mirroring updateDtg's "don't clobber in-progress edit" guard.
     */
    updateBasemapSelect(basemapStyleId = null) {
        if (this._basemapOpen) return;
        const next = basemapStyleId || BASEMAP_STYLES[0].url;
        if (this._basemapValue !== next) {
            this._basemapValue = next;
            this._renderHeader();
        }
    }

    /** Sync caption text from node attrs — skipped while the user is actively editing it. */
    updateCaption(caption) {
        if (!this._captionEl) return;
        if (document.activeElement === this._captionEl) return;
        const next = caption || '';
        if (this._captionEl.textContent !== next) this._captionEl.textContent = next;
    }

    _saveCaption() {
        this._onCaptionChange(this._captionEl.textContent || '');
    }

    _onCaptionBlur() {
        this._saveCaption();
    }

    _onCaptionKeydown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            this._saveCaption();
            this._captionEl.blur();
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            this._captionEl.blur();
        }
    }

    /**
     * Ticket 09: strip formatting — only allow plain text in the caption.
     */
    _onCaptionPaste(e) {
        pasteAsPlainText(this._captionEl, e);
    }

    _onBadgeClick(event) {
        const isBadgeClick = isClassificationBadgeClick(event, this.dom);
        if (!isBadgeClick) return;
        if (!this.classificationMenu) return;
        event.preventDefault();
        event.stopPropagation();
        openClassificationMenuForBlock({
            classificationMenu: this.classificationMenu,
            dom: this.dom,
            attrs: this._lastAttrs || {classificationLevel: 'U', distribution: [], compartment: []},
            event,
            applyResult: (result) => this._onClassificationChange(result),
        });
    }

    /** Keep attrs reference for badge menu when opened before first update. */
    rememberAttrs(attrs) {
        this._lastAttrs = attrs;
    }

    /**
     * Returns true if `target` is part of this chrome's interactive UI — used by MapNodeView's
     * stopEvent() and double-click guard to let PM know it should not intercept these events.
     *
     * Checks the header element (contains all 3 field triggers + the Edit button) and the caption
     * element. Also checks any currently-open MUI popup (Select menus / DatePicker calendar) which
     * render via Portal to document.body and are therefore NOT descendants of chrome.dom.
     */
    containsTarget(target) {
        if (this._headerEl?.contains(target)) return true;
        if (this._captionEl?.contains(target)) return true;
        // MINSKY-16410: the under-the-map progress row (Retry/Dismiss buttons) — same reasoning
        // as the header/caption checks above, so PM's stopEvent()/dblclick guard don't intercept
        // clicks on it.
        if (this._sitrepProgressEl?.contains(target)) return true;
        // Portaled popup roots — populated via callback refs while the popup is mounted:
        if (this._viewPopperEl?.contains(target)) return true;
        if (this._dtgPopperEl?.contains(target)) return true;
        if (this._basemapPopperEl?.contains(target)) return true;
        if (this._sitrepDialogEl?.contains(target)) return true;
        return false;
    }

    destroy() {
        this.dom.removeEventListener('mousedown', this._onBadgeClickBound);
        if (this._headerRoot) {
            // React 18 concurrent mode forbids synchronous unmount while a render is in progress
            // — defer to the next microtask, mirroring FloatingMenuTooltip.destroy()'s pattern.
            const root = this._headerRoot;
            this._headerRoot = null;
            queueMicrotask(() => root.unmount());
        }
        if (this._sitrepProgressRoot) {
            const root = this._sitrepProgressRoot;
            this._sitrepProgressRoot = null;
            queueMicrotask(() => root.unmount());
        }
    }
}
